import React, { useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from "jwt-decode";
import UnauthorizedAccess from './components/common/UnauthorizedAccess';
import AuthService from './services/AuthService';

const PrivateRoute = ({ element: Element, requiredGroup, requiredRole, ...rest }) => {
  const token = Cookies.get('token');
  const storedGroups = Cookies.get('groups');
  const auditRole = Cookies.get('audit_role');
  const username = AuthService.getUsername();
  const groups = storedGroups ? JSON.parse(storedGroups) : [];
  const location = useLocation();

  // Function to handle logout with error message
  const redirectToLogin = (errorMessage) => {
    // Log the error
    console.error('Authentication failed:', errorMessage);
    
    // Clear all authentication data instead of doing it in the return statement
    AuthService.logout();
    
    // Redirect to login with error message (this will be handled by AuthService.logout())
    return <Navigate to={`/loginform?error=${encodeURIComponent(errorMessage)}`} replace />;
  };

  // Effect to check token validity on route changes
  useEffect(() => {
    // Check token validity whenever route changes
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decodedToken.exp < currentTime) {
          console.warn('Token expired during navigation');
          // Force logout and redirect - using AuthService directly
          AuthService.logout();
        }
      } catch (error) {
        console.error('Token validation error during navigation:', error);
        // Force logout and redirect - using AuthService directly
        AuthService.logout();
      }
    }
  }, [location.pathname]);

  try {
    // Check if user is authenticated with a valid token
    if (!token) {
      return redirectToLogin('Your session has expired. Please log in again.');
    }

    // Verify token is not expired
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        // Token expired, redirect to login
        return redirectToLogin('Your session has expired. Please log in again.');
      }

      // Check for required token fields
      if (!decodedToken.user_id && !decodedToken.username) {
        return redirectToLogin('Invalid token format. Please log in again.');
      }

      // Authorization checks
      const hasRequiredGroup = requiredGroup && groups.includes(requiredGroup);
      const hasRequiredRole = requiredRole && auditRole === requiredRole;

      // Log the authorization check details for debugging
      if (requiredGroup || requiredRole) {
        console.log('Authorization check:', {
          username: username || decodedToken.username || 'unknown',
          userGroups: groups,
          userRole: auditRole,
          requiredGroup,
          requiredRole,
          hasRequiredGroup,
          hasRequiredRole,
        });
      }

      // If we only require a group, check if user has it
      if (requiredGroup && !requiredRole && hasRequiredGroup) {
        return <Outlet {...rest} />;
      }

      // If we only require a role, check if user has it
      if (requiredRole && !requiredGroup && hasRequiredRole) {
        return <Outlet {...rest} />;
      }

      // If we require both, check if user has both
      if (requiredGroup && requiredRole && hasRequiredGroup && hasRequiredRole) {
        return <Outlet {...rest} />;
      }

      // If no specific permissions required, just check authentication
      if (!requiredGroup && !requiredRole) {
        return <Outlet {...rest} />;
      }

      // User doesn't have required permissions - log the unauthorized access attempt
      console.warn('Unauthorized access attempt:', {
        username: username || decodedToken.username || 'unknown',
        userGroups: groups,
        userRole: auditRole,
        requiredGroup,
        requiredRole
      });
      
      return <UnauthorizedAccess />;
    } catch (jwtError) {
      // Handle JWT decode errors (malformed tokens)
      console.error("JWT decode error:", jwtError);
      return redirectToLogin('Invalid token format. Please log in again.');
    }
  } catch (error) {
    console.error("Authentication error:", error);
    return redirectToLogin('Authentication error. Please log in again.');
  }
};

export default PrivateRoute;

